import { Alert, Stack, TextField, TextFieldProps } from "@mui/material";
import { useEffect, useRef, useState } from "react";

interface MFAInputProps {
  onSubmit: (code: string) => Promise<void>;
}

export function MFAInput({ onSubmit }: MFAInputProps) {
  const [codeParts, setCodeParts] = useState<string[]>([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [allSelected, setAllSelected] = useState(false);
  const submittingRef = useRef(false);

  const focusInput = (index: number) => {
    document.getElementById(`mfa-input-${index}`)?.focus();
  };

  useEffect(() => {
    focusInput(0);
  }, []);

  const submit = async (code: string) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await onSubmit(code);
    } catch (error: unknown) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to verify code. Please try again.",
      );
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleChange = (index: number, newValue: string) => {
    if (newValue.length > 1) return;
    const newCodeParts = [...codeParts];
    newCodeParts[index] = newValue;
    setCodeParts(newCodeParts);
    if (newValue.length === 1) {
      focusInput(index + 1);
    }
    const everyFieldIsFilled = newCodeParts.every((part) => part.length === 1);
    if (everyFieldIsFilled) {
      submit(newCodeParts.join(""));
    }
  };

  const clearAndFocusPrevious = (index: number) => {
    if (index === 0) return;
    setCodeParts((prev) => {
      const next = [...prev];
      next[index - 1] = "";
      return next;
    });
    focusInput(index - 1);
  };

  const clearAll = () => {
    setCodeParts(["", "", "", "", "", ""]);
    setAllSelected(false);
    focusInput(0);
  };

  const moveFocus = (currentIndex: number, direction: -1 | 1) => {
    const target = currentIndex + direction;
    if (target < 0 || target >= codeParts.length) return;
    focusInput(target);
  };

  return (
    <Stack spacing={2} alignItems="center" sx={{ width: "100%" }}>
      <Stack direction="row" spacing={3} alignItems="center">
        {[codeParts.slice(0, 3), codeParts.slice(3)].map(
          (group, groupIndex) => (
            <Stack
              key={groupIndex}
              direction="row"
              spacing={1}
              alignItems="center"
            >
              {group.map((value, indexInGroup) => {
                const index = groupIndex * 3 + indexInGroup;
                return (
                  <MFAInputField
                    id={`mfa-input-${index}`}
                    key={index}
                    value={value}
                    onChange={(newValue) => handleChange(index, newValue)}
                    onBackspaceAtStart={() => clearAndFocusPrevious(index)}
                    onSelectAll={() => setAllSelected(true)}
                    onClearAll={clearAll}
                    onDeselect={() => setAllSelected(false)}
                    onMove={(direction) => moveFocus(index, direction)}
                    selected={allSelected}
                    InputProps={{ readOnly: submitting }}
                  />
                );
              })}
            </Stack>
          ),
        )}
      </Stack>
      {errorMessage && (
        <Alert severity="error" sx={{ width: "100%" }}>
          {errorMessage}
        </Alert>
      )}
    </Stack>
  );
}

function MFAInputField({
  onChange,
  onBackspaceAtStart,
  onSelectAll,
  onClearAll,
  onDeselect,
  onMove,
  selected,
  ...props
}: Omit<TextFieldProps, "inputProps" | "onChange"> & {
  onChange: (value: string) => void;
  onBackspaceAtStart: () => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  onDeselect: () => void;
  onMove: (direction: -1 | 1) => void;
  selected: boolean;
}) {
  return (
    <TextField
      {...props}
      inputProps={{ maxLength: 1, style: { textAlign: "center" } }}
      sx={{
        width: 52,
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          ...(selected && {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "primary.main",
            },
          }),
        },
        "& .MuiInputBase-input": {
          fontSize: "1.75rem",
          fontWeight: 600,
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          padding: "14px 0",
          borderRadius: 2,
          transition: "background-color 0.15s ease, color 0.15s ease",
          ...(selected && {
            backgroundColor: "primary.main",
            color: "primary.contrastText",
            caretColor: "transparent",
          }),
        },
        ...(!selected && {
          "& .MuiOutlinedInput-root.Mui-focused .MuiInputBase-input": {
            backgroundColor: "text.primary",
            color: "background.paper",
            caretColor: "background.paper",
          },
        }),
      }}
      onChange={(e) => onChange(e.target.value)}
      onMouseDown={() => {
        if (selected) onDeselect();
      }}
      onKeyDown={(e) => {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
          e.preventDefault();
          onSelectAll();
          return;
        }
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          if (selected) onDeselect();
          onMove(-1);
          return;
        }
        if (e.key === "ArrowRight") {
          e.preventDefault();
          if (selected) onDeselect();
          onMove(1);
          return;
        }
        if (selected) {
          if (e.key === "Backspace" || e.key === "Delete") {
            e.preventDefault();
            onClearAll();
            return;
          }
          onDeselect();
          return;
        }
        const input = e.target as HTMLInputElement;
        if (
          e.key === "Backspace" &&
          input.selectionStart === 0 &&
          input.selectionEnd === 0
        ) {
          e.preventDefault();
          onBackspaceAtStart();
        }
      }}
    />
  );
}
